import React from "react";
import { Button, Spinner } from "react-bootstrap";
import { PermissionWrapper } from "../../../components/PermissionWrapper/PermissionWrapper";
import { ENROLMENTS_MANAGE_PERMS } from "../permissions";
import { useEnrolmentForm, type EnrolmentFormMode } from "./useEnrolmentForm";
import type { EnrolmentDetails, BatchDetails } from "../../../api/academyEnrolments/enrolmentsService";

import PhoneSearchSection from "./sections/PhoneSearchSection";
import RegisterBySection from "./sections/RegisterBySection";
import PlayerSelectSection from "./sections/PlayerSelectSection";
import AcademySection from "./sections/AcademySection";
import CenterSection from "./sections/CenterSection";
import AgeCategorySection from "./sections/AgeCategorySection";
import BatchSection from "./sections/BatchSection";
import SlotSection from "./sections/SlotSection";
import FrequencyAndPaymentSection from "./sections/FrequencyAndPaymentSection";
import StartDateSection from "./sections/StartDateSection";
import SummaryAside from "./sections/SummaryAside";

import "./EnrolmentForm.css";

interface EnrolmentFormProps {
  mode: EnrolmentFormMode;
  initialEnrolment?: EnrolmentDetails | null;
  initialBatch?: BatchDetails | null;
  onSuccess?: () => void;
}

const EnrolmentForm: React.FC<EnrolmentFormProps> = ({
  mode,
  initialEnrolment,
  initialBatch,
  onSuccess,
}) => {
  const {
    state,
    loading,
    submitError,
    setSubmitError,
    filteredPlayers,
    filteredBatchList,
    availablePaymentFrequencies,
    planFields,
    handlers,
  } = useEnrolmentForm({ mode, initialEnrolment, initialBatch, onSuccess });

  const isView = mode === "view";
  const isAdd = mode === "add";
  const isEdit = mode === "edit";

  // ── Section unlock flags ─────────────────────────────────────────────────
  const batch = state.selectedBatch;
  const slotType = batch?.slotType ?? (initialEnrolment?.slotType ?? "fixed");
  const fixedSlots = batch?.fixedSlots ?? [];
  const dynamicSlots = (batch?.slots ?? []) as Parameters<typeof SlotSection>[0]["dynamicSlots"];
  const fees = batch?.fees ?? [];

  const isDynamic = slotType === "dynamic";

  const hasUser = !!state.searchedProfileData || !isAdd;
  const hasRegisterBy = !!state.registerBy;
  const hasPlayer = !!state.selectedPlayer || !isAdd;
  const hasAcademy = !!state.selectedAcademy;
  const hasSport = !!state.selectedSport;
  const hasCenter = !!state.selectedCenter;
  const hasAgeCategory = !!state.selectedAgeCategory;
  const hasBatch = !!state.selectedBatch;
  const hasSlot = isDynamic
    ? state.selectedDynamicSlots.length > 0
    : !!state.selectedSlot;
  const hasTrainingFreq = !!state.selectedTrainingFrequency;

  const hasValidPaymentAmount = (() => {
    const amt = parseFloat(state.paymentAmount);
    return !!state.paymentAmount && !isNaN(amt) && amt > 0;
  })();

  const canSubmit =
    (isAdd &&
      hasUser &&
      hasRegisterBy &&
      !!state.selectedPlayer &&
      hasAcademy &&
      hasSport &&
      hasCenter &&
      hasBatch &&
      hasSlot &&
      hasTrainingFreq &&
      !!state.selectedPaymentFrequency &&
      !!state.startDate &&
      hasValidPaymentAmount) ||
    (isEdit &&
      hasAcademy &&
      hasCenter &&
      hasAgeCategory &&
      hasBatch &&
      hasTrainingFreq &&
      !!state.selectedPaymentFrequency &&
      !!state.startDate &&
      hasValidPaymentAmount);

  const handleSubmitClick = async () => {
    setSubmitError(null);
    await handlers.handleSubmit();
  };

  return (
    <div className="enrolment-form-layout">
      {/* Main form column */}
      <div>
        {/* Section 1 — Phone */}
        <PhoneSearchSection
          state={state}
          loading={loading}
          mode={mode}
          onPhoneChange={handlers.setPhoneNumber}
          onSearch={handlers.handlePhoneSearch}
          onShowAddProfile={handlers.setShowAddProfileForm}
          onAddProfile={handlers.handleAddProfile}
          setNewParentName={handlers.setNewParentName}
          setNewParentGender={handlers.setNewParentGender}
          setNewParentDob={handlers.setNewParentDob}
          setNewPlayerName={handlers.setNewPlayerName}
          setNewPlayerGender={handlers.setNewPlayerGender}
          setNewPlayerDob={handlers.setNewPlayerDob}
          registerBy={state.registerBy}
        />

        {/* Section 2 — Register by (add mode only) */}
        {!isEdit && (
          <div className={`enrolment-form-section${!hasUser && isAdd ? " locked" : ""}`}>
            <h6 className="enrolment-section-title">
              <span className="enrolment-section-number">2</span>
              Registering As
            </h6>
            {hasUser ? (
              <RegisterBySection
                value={state.registerBy}
                onChange={handlers.handleRegisterByChange}
                disabled={isView}
              />
            ) : (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                Complete phone search first.
              </p>
            )}
          </div>
        )}

        {/* Section 3 — Player */}
        <div className={`enrolment-form-section${!hasRegisterBy && isAdd ? " locked" : ""}`}>
          <h6 className="enrolment-section-title">
            <span className="enrolment-section-number">3</span>
            {state.registerBy === "parent" ? "Select Player (Child)" : "Select Player Profile"}
          </h6>
          {hasRegisterBy || !isAdd ? (
            <PlayerSelectSection
              players={filteredPlayers}
              selectedPlayer={state.selectedPlayer}
              onSelect={handlers.handlePlayerSelect}
              disabled={isView || isEdit}
              registerBy={state.registerBy}
            />
          ) : (
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              Select who is registering first.
            </p>
          )}
        </div>

        {/* Section 4 — Academy + Sport */}
        {(hasPlayer || !isAdd) && (
          <AcademySection
            academyList={state.academyList}
            selectedAcademy={state.selectedAcademy}
            selectedSport={state.selectedSport}
            loadingAcademies={loading.academies}
            onAcademySelect={handlers.handleAcademySelect}
            onSportSelect={handlers.handleSportSelect}
            disabled={isView}
          />
        )}

        {/* Section 5 — Center */}
        {((hasAcademy && hasSport) || !isAdd) && (
          <CenterSection
            centerList={state.centerList}
            selectedCenter={state.selectedCenter}
            loadingCenters={loading.centers}
            onCenterSelect={handlers.handleCenterSelect}
            disabled={isView}
          />
        )}

        {/* Section 6 — Age Category */}
        {(hasCenter || !isAdd) && (
          <AgeCategorySection
            ageCategoryList={state.ageCategoryList}
            selectedAgeCategory={state.selectedAgeCategory}
            loadingAgeCategories={loading.ageCategories}
            onAgeCategorySelect={handlers.handleAgeCategorySelect}
            disabled={isView}
          />
        )}

        {/* Section 7 — Batch */}
        {(hasAgeCategory || !isAdd) && (
          <BatchSection
            batchList={filteredBatchList}
            selectedBatch={state.selectedBatch}
            loadingBatches={loading.batches}
            onBatchSelect={handlers.handleBatchSelect}
            disabled={isView}
            sectionNumber={7}
          />
        )}

        {/* Section 8 — Slot */}
        {(hasBatch || !isAdd) && (
          <SlotSection
            slotType={slotType}
            fixedSlots={fixedSlots}
            dynamicSlots={dynamicSlots}
            selectedSlot={state.selectedSlot}
            onSlotSelect={handlers.handleSlotSelect}
            selectedDynamicSlots={state.selectedDynamicSlots}
            onDynamicSlotToggle={handlers.handleDynamicSlotToggle}
            disabled={isView}
            sectionNumber={8}
          />
        )}

        {/* Section 9 — Training + Payment Frequency */}
        {((hasSlot && fees.length > 0) || !isAdd) && (
          <FrequencyAndPaymentSection
            fees={fees}
            selectedTrainingFrequency={state.selectedTrainingFrequency}
            selectedPaymentFrequency={state.selectedPaymentFrequency}
            availablePaymentFrequencies={availablePaymentFrequencies}
            planFields={planFields}
            onTrainingFrequencySelect={handlers.handleTrainingFrequencySelect}
            onPaymentFrequencySelect={handlers.handlePaymentFrequencySelect}
            disabled={isView}
            sectionNumber={9}
          />
        )}

        {/* Section 10 — Start Date & Payment */}
        {((hasTrainingFreq && !!state.selectedPaymentFrequency) || !isAdd) && (
          <StartDateSection
            value={state.startDate}
            onChange={handlers.handleStartDateChange}
            paymentAmount={state.paymentAmount}
            onPaymentAmountChange={handlers.handlePaymentAmountChange}
            disabled={isView}
            sectionNumber={10}
          />
        )}

        {/* Submit error */}
        {submitError && (
          <div className="enrolment-submit-error mb-3">
            <i className="ri-error-warning-line me-2" />
            {submitError}
          </div>
        )}

        {/* Submit button */}
        {!isView && (
          <PermissionWrapper
            anyOfPermissions={ENROLMENTS_MANAGE_PERMS}
            hideWhenDisabled={false}
          >
            <Button
              variant="primary"
              disabled={!canSubmit || loading.submit}
              onClick={handleSubmitClick}
              style={{ minWidth: 140 }}
            >
              {loading.submit && (
                <Spinner size="sm" animation="border" className="me-2" />
              )}
              {isAdd ? "Create Enrolment" : "Update Enrolment"}
            </Button>
          </PermissionWrapper>
        )}
      </div>

      {/* Summary aside */}
      <SummaryAside state={state} mode={mode} planFields={planFields} />
    </div>
  );
};

export default EnrolmentForm;
